import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import isEqual from "lodash-es/isEqual";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import type { RootState } from "@/store/app/storeTypes";
import { getEditedContentLabels } from "@/features/sparxsEditor/lib/form/formEditedPaths";
import { prepareBlueprintForEditor } from "@/features/sparxsEditor/lib/blueprint/blueprintNormalize";
import {
  activeSparxSelectionResolved,
  sparxsEditorBlueprintFetchedHydrated,
  sparxsEditorConflictResolvedWithServerBlueprint,
  sparxsEditorRevertedSessionFileToOriginal,
  sparxsEditorSaveSucceededForFiles,
} from "@/features/sparxsEditor/store/domainActions";

export interface SessionSparxEntry {
  original: InteractionBlueprintData;
  current: InteractionBlueprintData;
  /** Content form labels (e.g. intro, note) that differ from original. Persisted so we remember which components changed when file is reopened. Omitted on older persisted entries; computed from current/original when needed. */
  editedContentLabels?: string[];
  /** Server last-modified (ms) when we loaded this file; used for timestamp-based conflict detection. */
  lastKnownServerModified?: number;
}

export interface SparxSessionState {
  byFileName: Record<string, SessionSparxEntry>;
}

const initialState: SparxSessionState = {
  byFileName: {},
};

const computeEditedContentLabels = (
  current: InteractionBlueprintData,
  original: InteractionBlueprintData,
): string[] => getEditedContentLabels(current, original);

/** True when the user has unsaved edits (Save-enabled): current differs from original baseline. */
export const isSessionEntryDirty = (entry: SessionSparxEntry): boolean =>
  !isEqual(entry.current, entry.original);

/** Entries with unsaved edits only; unchanged files are re-fetched, so they are not worth persisting. */
export const pickDirtySessionEntries = (
  byFileName: Record<string, SessionSparxEntry>,
): Record<string, SessionSparxEntry> =>
  Object.fromEntries(
    Object.entries(byFileName).filter(([, entry]) =>
      isSessionEntryDirty(entry),
    ),
  );

/** Derived from session data: file names where current !== original. Never stored. */
const getModifiedFileNamesFromByFileName = (
  byFileName: Record<string, SessionSparxEntry>,
): string[] =>
  Object.keys(byFileName).filter((fileName) => {
    const entry = byFileName[fileName];
    return entry != null && isSessionEntryDirty(entry);
  });

/** Selector: files with unsaved session changes (current differs from original). */
export const selectModifiedFileNames = createSelector(
  [(state: RootState) => state.editor.sparxSession.byFileName],
  (byFileName) => getModifiedFileNamesFromByFileName(byFileName),
);

type SetSessionSparxPayload = {
  fileName: string;
  original: InteractionBlueprintData;
  current: InteractionBlueprintData;
  lastKnownServerModified?: number;
};

const mutateSetSessionSparx = (
  state: SparxSessionState,
  payload: SetSessionSparxPayload,
): void => {
  const { fileName, original, current, lastKnownServerModified } = payload;
  const preparedOriginal = prepareBlueprintForEditor(original);
  const preparedCurrent = prepareBlueprintForEditor(current);
  state.byFileName[fileName] = {
    original: preparedOriginal,
    current: preparedCurrent,
    editedContentLabels: computeEditedContentLabels(
      preparedCurrent,
      preparedOriginal,
    ),
    ...(lastKnownServerModified != null && {
      lastKnownServerModified,
    }),
  };
};

const mutateRevertSessionSparx = (
  state: SparxSessionState,
  fileName: string,
): void => {
  const entry = state.byFileName[fileName];
  if (entry == null) {
    return;
  }
  state.byFileName[fileName] = {
    original: entry.original,
    current: entry.original,
    editedContentLabels: [],
    ...(entry.lastKnownServerModified != null && {
      lastKnownServerModified: entry.lastKnownServerModified,
    }),
  };
};

const sparxSessionSlice = createSlice({
  name: "sparxSession",
  initialState,
  reducers: {
    setSessionSparx: (state, action: PayloadAction<SetSessionSparxPayload>) => {
      mutateSetSessionSparx(state, action.payload);
    },
    /**
     * Updates the current (working) blueprint for a file. Payload is cloned and
     * normalized inside the reducer; callers may pass raw data (e.g. from form
     * getValues() or session).
     */
    updateSessionCurrent: (
      state,
      action: PayloadAction<{
        fileName: string;
        data: InteractionBlueprintData;
      }>,
    ) => {
      const { fileName, data } = action.payload;
      const entry = state.byFileName[fileName];
      if (entry == null) return;
      const preparedData = prepareBlueprintForEditor(data);
      state.byFileName[fileName] = {
        ...entry,
        current: preparedData,
        editedContentLabels: computeEditedContentLabels(
          preparedData,
          entry.original,
        ),
      };
    },
    revertSessionSparx: (state, action: PayloadAction<string>) => {
      mutateRevertSessionSparx(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(activeSparxSelectionResolved, (state, action) => {
        const persist = action.payload.persistDirtySession;
        if (persist == null) {
          return;
        }
        const { fileName, data } = persist;
        const entry = state.byFileName[fileName];
        if (entry == null) {
          return;
        }
        const preparedData = prepareBlueprintForEditor(data);
        state.byFileName[fileName] = {
          ...entry,
          current: preparedData,
          editedContentLabels: computeEditedContentLabels(
            preparedData,
            entry.original,
          ),
        };
      })
      .addCase(sparxsEditorSaveSucceededForFiles, (state, action) => {
        for (const row of action.payload.saved) {
          mutateSetSessionSparx(state, {
            fileName: row.fileName,
            original: row.blueprint,
            current: row.blueprint,
            lastKnownServerModified: row.serverLastModifiedMs,
          });
        }
      })
      .addCase(sparxsEditorRevertedSessionFileToOriginal, (state, action) => {
        mutateRevertSessionSparx(state, action.payload.fileName);
      })
      .addCase(
        sparxsEditorConflictResolvedWithServerBlueprint,
        (state, action) => {
          const { fileName, blueprint, lastKnownServerModified } =
            action.payload;
          mutateSetSessionSparx(state, {
            fileName,
            original: blueprint,
            current: blueprint,
            lastKnownServerModified,
          });
        },
      )
      .addCase(sparxsEditorBlueprintFetchedHydrated, (state, action) => {
        const { fileName, blueprint, lastKnownServerModified } = action.payload;
        const entry = state.byFileName[fileName];
        if (entry != null && isSessionEntryDirty(entry)) {
          return;
        }
        mutateSetSessionSparx(state, {
          fileName,
          original: blueprint,
          current: blueprint,
          lastKnownServerModified,
        });
      });
  },
});

export const { setSessionSparx, updateSessionCurrent, revertSessionSparx } =
  sparxSessionSlice.actions;
export default sparxSessionSlice.reducer;
